import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { BrandLogo, BrandWordmark } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  LayoutGrid, Palette, Lightbulb, ClipboardList, Sparkles, BarChart3,
  CalendarClock, FolderKanban, Library, Building2, Check, ArrowRight,
  Lock, Brain, ListChecks, ShieldCheck, Quote,
  Landmark, Search, Bookmark, ExternalLink, FileText, UserCheck,
} from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1601642964568-1917224f4e4d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const PRODUCT_VIEWS = [
  { src: "/product/dashboard.png", label: "Business Dashboard", desc: "See where your business stands, what needs attention, and what to work on next." },
  { src: "/product/content.png", label: "Content Command Center", desc: "Turn one weekly theme into content for every platform you actually use." },
  { src: "/product/ai-guide.png", label: "AI Business Guide", desc: "Strategy, drafts and answers built from your own business, not generic advice." },
];

const MODULES = [
  { icon: Landmark, title: "Grant Finder", desc: "Discover verified federal grant opportunities that may match your business profile, with official links, deadlines and rule-based matching." },
  { icon: LayoutGrid, title: "Content Command Center", desc: "Turn one weekly theme into platform-ready content across every channel you actually use." },
  { icon: Sparkles, title: "AI Business Guide", desc: "A strategist that already knows your business, brand voice and current numbers." },
  { icon: Palette, title: "Brand Center", desc: "Your voice, framework, words to use and avoid, all in one dependable place." },
  { icon: Lightbulb, title: "Idea Vault", desc: "Capture ideas the moment they arrive and turn them into finished work." },
  { icon: ClipboardList, title: "SOP Library", desc: "Write down how you do things once so the business runs without re-deciding." },
  { icon: FolderKanban, title: "Projects", desc: "Keep initiatives, tasks and next steps organized instead of scattered." },
  { icon: Library, title: "Knowledge Library", desc: "Store the documents and references your business relies on." },
  { icon: BarChart3, title: "Business Center", desc: "Track revenue, expenses, offers and goals with numbers you enter and own." },
  { icon: CalendarClock, title: "Planning", desc: "Set the week and the month so you always know what to work on next." },
  { icon: Building2, title: "Business Setup", desc: "One guided profile that grounds your whole workspace and the AI Guide." },
];

const GRANT_FEATURES = [
  { icon: Building2, title: "Business funding profile", desc: "Build a reusable profile — location, industry, years in business, revenue, team size, funding needs and applicant type." },
  { icon: ShieldCheck, title: "Verified grant opportunities", desc: "Opportunities come from official public sources, so you review real listings rather than invented or unverified data." },
  { icon: Search, title: "Rule-based profile matching", desc: "A transparent, rule-based check compares your profile against each listing to show which ones may match your business profile." },
  { icon: UserCheck, title: "Eligibility details", desc: "See the eligibility information the official source provides, in plain language, so you can decide what to pursue." },
  { icon: CalendarClock, title: "Deadlines", desc: "See the application deadline where the source lists one, or a clear note to verify it on the official page." },
  { icon: ExternalLink, title: "Official source & application links", desc: "Every opportunity links straight to its official source and application information for the details that matter." },
  { icon: Bookmark, title: "Save, status & notes", desc: "Save opportunities, set your own status, add private notes, and sort by deadline to stay organized." },
];

const STEPS = [
  { n: "01", title: "Complete your Business Setup", desc: "A short guided wizard captures who you serve, what you offer and how you sound." },
  { n: "02", title: "Bring your business into one place", desc: "Add content, brand, SOPs, projects, knowledge, planning and your real numbers." },
  { n: "03", title: "Work with your AI Business Guide", desc: "Get strategy, drafts and answers built from your own business, not generic advice." },
];

const BRINGS = [
  "Grant Finder to discover funding opportunities that may match your profile",
  "Content and a full week of platform-ready posts",
  "Brand voice, framework and standards",
  "Standard operating procedures",
  "Projects, tasks and next steps",
  "Knowledge and reference documents",
  "Weekly and monthly planning",
  "Revenue, expenses, offers and goals",
  "The business context that grounds your AI Guide",
];

const FAQ = [
  { q: "What exactly is Work & Keep?", a: "A guided business operating system for solo founders. It brings your content, processes, projects, knowledge, planning and business information into one private workspace, so you can run the whole business without relying on scattered tools and memory." },
  { q: "How does the pricing work?", a: "There is a one-time $149 setup and onboarding fee, then $70/month. Your first payment is $219 before any applicable tax. You can manage or cancel your subscription anytime from the customer portal." },
  { q: "Is my workspace private to me?", a: "Yes. Every customer receives their own private, isolated workspace. Your data is scoped to your account only, and it is never mixed with or visible to any other customer." },
  { q: "How does the AI Business Guide know my business?", a: "It works only from your own business information, your Business Setup, brand, content, and the numbers you enter. It does not invent facts or pull from other businesses, so its guidance stays specific to you." },
  { q: "Do I own my data?", a: "Yes. The information you add is yours, and you can export it from your workspace whenever you like." },
  { q: "Who is Work & Keep for?", a: "Solo founders and one-person businesses who want a calmer, more organized way to run everything in one place." },
];

function Nav({ onGetStarted }) {
  return (
    <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-md border-b border-border">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 h-28 flex items-center justify-between">
        <BrandLogo className="h-20 sm:h-24 w-auto max-w-[380px] object-contain object-left" />
        <div className="flex items-center gap-2 sm:gap-4">
          <Link to="/login" data-testid="nav-signin-link"
            className="text-sm font-medium text-foreground/70 hover:text-foreground transition-colors px-2">
            Sign in
          </Link>
          <Button onClick={onGetStarted} data-testid="nav-get-started-button"
            className="rounded-full h-10 px-5 bg-primary hover:bg-primary/90 text-primary-foreground text-sm">
            Get started
          </Button>
        </div>
      </div>
    </header>
  );
}

export default function LandingPage() {
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    api.get("/public/plan").then((r) => setPlan(r.data)).catch(() => setPlan(null));
  }, []);

  const purchase = () => {
    if (plan?.payment_link) {
      window.location.href = plan.payment_link;
    } else {
      window.location.href = "/login";
    }
  };

  const setupFee = plan?.plan?.setup_fee ?? 149;
  const monthly = plan?.plan?.monthly ?? 70;
  const firstPayment = plan?.first_payment ?? 219;

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-clip" data-testid="landing-page">
      <Nav onGetStarted={purchase} />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="animate-gentle-fade">
            <span className="inline-flex items-center gap-2 rounded-full bg-muted px-4 py-1.5 text-xs font-medium text-primary uppercase tracking-wide">
              For solo founders
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-6 text-foreground">
              Do the work.<br />Keep the business together.
            </h1>
            <p className="font-accent text-lg text-primary mt-4">The Business Operating System for solo founders.</p>
            <p className="text-base text-foreground/75 mt-5 max-w-lg leading-relaxed">
              Work &amp; Keep is a guided operating system that brings your content, processes, projects,
              knowledge, planning and business information into one private workspace, so you stop relying
              on scattered tools and memory.
            </p>
            <a href="#grant-finder" data-testid="hero-grant-finder-note"
              className="mt-5 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-4 py-2 text-sm text-primary hover:bg-primary/10 transition-colors">
              <Landmark className="h-4 w-4" strokeWidth={1.75} />
              <span><span className="font-semibold">Now includes Grant Finder</span> — federal grant opportunities that may match your business profile</span>
            </a>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Button onClick={purchase} data-testid="hero-purchase-button"
                className="rounded-full h-12 px-7 bg-primary hover:bg-primary/90 text-primary-foreground text-base gap-2 shadow-soft">
                Get started, ${firstPayment} today <ArrowRight className="h-4 w-4" />
              </Button>
              <Link to="/login" data-testid="hero-signin-link"
                className="inline-flex items-center justify-center rounded-full h-12 px-7 border border-border text-base font-medium hover:bg-muted transition-colors">
                Sign in
              </Link>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              ${firstPayment} due today — includes your ${setupFee} setup + first month. Then ${monthly}/month. Secure checkout via Stripe.
            </p>
          </div>

          <div className="relative animate-gentle-fade" style={{ animationDelay: "120ms" }}>
            <div className="absolute -inset-4 bg-accent/40 rounded-[2.5rem] rotate-2" />
            <img src={HERO_IMG} alt="A calm, organized workspace"
              className="relative rounded-[2rem] shadow-soft-lg w-full h-[380px] lg:h-[460px] object-cover" />
          </div>
        </div>
      </section>

      {/* Inside Work & Keep — real product views */}
      <section className="mx-auto max-w-6xl px-5 sm:px-8 pb-6 lg:pb-10 -mt-4 lg:-mt-8" data-testid="product-views">
        <div className="max-w-2xl">
          <span className="text-xs font-semibold uppercase tracking-wide text-primary">A real operating workspace</span>
          <h2 className="font-heading text-3xl sm:text-4xl text-foreground mt-3">See inside Work &amp; Keep</h2>
          <p className="text-base text-foreground/70 mt-4 leading-relaxed">
            Not a planner or a chat box, but a workspace where your whole business lives together. Here are a few of the rooms inside.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-10">
          {PRODUCT_VIEWS.map((v, i) => {
            const Icon = [BarChart3, LayoutGrid, Sparkles][i];
            return (
              <figure key={v.label} className="group rounded-3xl border border-border bg-card shadow-soft overflow-hidden transition-transform duration-300 hover:-translate-y-1" data-testid="product-view-card">
                <div className="flex items-center gap-1.5 px-4 py-3 border-b border-border bg-muted/40">
                  <span className="h-2.5 w-2.5 rounded-full bg-accent" />
                  <span className="h-2.5 w-2.5 rounded-full bg-primary/30" />
                  <span className="h-2.5 w-2.5 rounded-full bg-border" />
                </div>
                <div className="aspect-[16/10] overflow-hidden bg-background">
                  <img src={v.src} alt={`${v.label} inside Work & Keep`} loading="lazy"
                    className="w-full h-full object-cover object-top" />
                </div>
                <figcaption className="p-6">
                  <span className="inline-flex items-center gap-2">
                    <Icon className="h-4 w-4 text-primary" strokeWidth={1.75} />
                    <span className="font-heading text-lg text-foreground">{v.label}</span>
                  </span>
                  <p className="text-sm text-foreground/70 mt-2 leading-relaxed">{v.desc}</p>
                </figcaption>
              </figure>
            );
          })}
        </div>
      </section>

      {/* Grant Finder — featured */}
      <section id="grant-finder" className="scroll-mt-28 mx-auto max-w-6xl px-5 sm:px-8 py-20 lg:py-24" data-testid="grant-finder-section">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <div>
            <span className="inline-flex items-center gap-2 text-primary">
              <Landmark className="h-5 w-5" strokeWidth={1.75} />
              <span className="text-xs font-semibold uppercase tracking-wide">Included · Grant Finder</span>
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl text-foreground mt-4">
              Discover grant opportunities that may match your business profile
            </h2>
            <p className="text-base text-foreground/75 mt-5 leading-relaxed">
              Build a funding profile once, then explore verified federal grant opportunities drawn from
              official public sources. Work &amp; Keep uses transparent, rule-based matching to surface listings
              that <span className="font-semibold text-foreground">may match your business profile</span> — with
              eligibility details, deadlines and links to the official source.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-6">
              {GRANT_FEATURES.map((f) => {
                const Icon = f.icon;
                return (
                  <div key={f.title} className="flex items-start gap-3" data-testid="grant-feature-item">
                    <span className="mt-0.5 h-9 w-9 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                      <Icon className="h-5 w-5 text-primary" strokeWidth={1.75} />
                    </span>
                    <div>
                      <h3 className="font-heading text-base text-foreground leading-tight">{f.title}</h3>
                      <p className="text-sm text-foreground/70 mt-1 leading-relaxed">{f.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground mt-8 leading-relaxed max-w-lg">
              Work &amp; Keep helps you discover and organize grant opportunities. It does not promise
              eligibility, approval, or funding, and always points you to the official source to confirm the details.
            </p>
          </div>

          {/* Illustrative, fictional example mockup — not a screenshot of the live app */}
          <div className="relative lg:sticky lg:top-32">
            <div className="absolute -inset-4 bg-accent/30 rounded-[2.5rem] -rotate-2" />
            <div className="relative rounded-[2rem] border border-border bg-card shadow-soft-lg overflow-hidden" data-testid="grant-finder-mockup" aria-label="Illustrative example of Grant Finder with fictional data">
              <div className="flex items-center gap-1.5 px-4 py-3 border-b border-border bg-muted/40">
                <span className="h-2.5 w-2.5 rounded-full bg-accent" />
                <span className="h-2.5 w-2.5 rounded-full bg-primary/30" />
                <span className="h-2.5 w-2.5 rounded-full bg-border" />
                <span className="ml-3 text-xs text-muted-foreground">Grant Finder — illustrative example</span>
              </div>
              <div className="p-6 space-y-4">
                <div className="rounded-2xl border border-border bg-background p-5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-amber-100 text-amber-700">
                      <Check className="h-3.5 w-3.5" /> May match your profile
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                      <ShieldCheck className="h-3.5 w-3.5 text-primary" /> Official source
                    </span>
                    <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-primary/15 text-primary">Saved</span>
                  </div>
                  <h4 className="font-heading text-lg text-foreground leading-snug mt-3">Main Street Small Business Growth Grant</h4>
                  <p className="text-sm text-muted-foreground mt-1">Example Program Office (fictional)</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-4 text-sm">
                    <div><span className="text-muted-foreground">Funding:</span> <span className="text-foreground">Up to $25,000</span></div>
                    <div><span className="text-muted-foreground">Deadline:</span> <span className="text-foreground">Mar 31, 2026</span></div>
                    <div className="col-span-2"><span className="text-muted-foreground">Eligibility:</span> <span className="text-foreground">Small businesses (for-profit)</span></div>
                  </div>
                  <div className="mt-4 rounded-xl bg-primary/5 p-3">
                    <p className="text-[11px] font-semibold text-primary uppercase tracking-wide mb-1">Why this may match</p>
                    <p className="text-xs text-foreground/80">Source lists small businesses as eligible · mentions your industry area · currently open</p>
                  </div>
                  <div className="flex items-center gap-3 mt-4 text-sm">
                    <span className="inline-flex items-center gap-1.5 font-medium text-primary"><ExternalLink className="h-4 w-4" /> Official source</span>
                    <span className="inline-flex items-center gap-1.5 font-medium text-primary"><FileText className="h-4 w-4" /> Application info</span>
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground text-center leading-relaxed">
                  Illustrative example with fictional grant data. Not a real opportunity, and not a screenshot of the app.
                  Work &amp; Keep does not guarantee eligibility, approval, or funding.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem */}
      <section className="bg-secondary text-secondary-foreground">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 py-20 lg:py-24 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl leading-tight">
            Running a business alone means holding everything in your head.
          </h2>
          <p className="text-base sm:text-lg text-secondary-foreground/85 mt-6 leading-relaxed max-w-2xl mx-auto">
            Content lives in one app, ideas in another, your numbers in a spreadsheet, and your processes
            nowhere at all. Nothing talks to each other, so you spend your energy remembering and
            re-deciding instead of doing the work that grows the business.
          </p>
          <p className="font-accent text-lg text-accent mt-8">
            Work &amp; Keep gives it all one calm home.
          </p>
        </div>
      </section>

      {/* What it brings together */}
      <section className="mx-auto max-w-6xl px-5 sm:px-8 py-20 lg:py-24">
        <div className="max-w-2xl">
          <h2 className="font-heading text-3xl sm:text-4xl text-foreground">What Work &amp; Keep brings together</h2>
          <p className="text-base text-foreground/70 mt-4 leading-relaxed">
            One workspace for the whole business, so everything you need is in the same place and works together.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 gap-x-10 gap-y-4 mt-10">
          {BRINGS.map((b) => (
            <div key={b} className="flex items-start gap-3 py-2" data-testid="brings-item">
              <span className="mt-0.5 h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <Check className="h-3.5 w-3.5 text-primary" />
              </span>
              <span className="text-foreground/90 leading-relaxed">{b}</span>
            </div>
          ))}
        </div>
      </section>

      {/* AI uses your own context */}
      <section className="bg-muted">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 py-20 lg:py-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="order-2 lg:order-1 space-y-4" data-testid="ai-before-after">
            <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Generic AI</span>
              <p className="text-sm text-foreground/60 mt-3 leading-relaxed italic">
                &ldquo;To grow, post consistently, niche down, and start an email list.&rdquo;
              </p>
              <p className="text-xs text-muted-foreground mt-3">Advice that could apply to almost any business.</p>
            </div>
            <div className="rounded-3xl border border-primary/30 bg-primary/5 p-6 shadow-soft">
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-primary">
                <Sparkles className="h-3.5 w-3.5" /> Work &amp; Keep
              </span>
              <p className="text-sm text-foreground/85 mt-3 leading-relaxed">
                &ldquo;Working from your Business Setup, brand voice and audience, here&apos;s a next step for your
                offers and this week&apos;s priorities, written in your voice, using the numbers and notes
                you&apos;ve already entered.&rdquo;
              </p>
              <p className="text-xs text-primary/80 mt-3">Guidance shaped by the context you&apos;ve already given it.</p>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <span className="inline-flex items-center gap-2 text-primary">
              <Brain className="h-5 w-5" />
              <span className="text-xs font-semibold uppercase tracking-wide">Your context, not generic advice</span>
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl text-foreground mt-4">
              An AI Business Guide that actually knows your business
            </h2>
            <p className="text-base text-foreground/75 mt-5 leading-relaxed">
              The AI Business Guide uses the business context you&rsquo;ve entered&mdash;including your Business
              Setup, brand voice, audience, offers, recorded numbers, and Knowledge Library documents&mdash;to
              give guidance that fits your business.
            </p>
            <ul className="mt-7 space-y-3">
              {[
                "Grounded in your Business Setup, brand voice and audience",
                "Uses your offers, recorded numbers and Knowledge Library documents",
                "Remembers the context you've already entered, so you re-explain less",
                "Designed to stay grounded in the facts, numbers and context you provide",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <ListChecks className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <span className="text-foreground/90 leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="mx-auto max-w-6xl px-5 sm:px-8 py-20 lg:py-24">
        <h2 className="font-heading text-3xl sm:text-4xl text-foreground text-center">How it works</h2>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {STEPS.map((s) => (
            <div key={s.n} className="rounded-3xl border border-border bg-card p-8 shadow-soft" data-testid="how-step">
              <span className="font-heading text-4xl text-accent">{s.n}</span>
              <h3 className="font-heading text-xl text-foreground mt-4">{s.title}</h3>
              <p className="text-sm text-foreground/70 mt-3 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Modules */}
      <section className="bg-muted">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 py-20 lg:py-24">
          <div className="max-w-2xl">
            <h2 className="font-heading text-3xl sm:text-4xl text-foreground">Your business operations, in one place.</h2>
            <p className="text-base text-foreground/70 mt-4 leading-relaxed">
              Each module is designed for a one-person business, and they all share the same business context.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {MODULES.map((m) => {
              const Icon = m.icon;
              return (
                <div key={m.title} className="rounded-3xl bg-card border border-border p-7 shadow-soft transition-transform duration-300 hover:-translate-y-1"
                  data-testid="module-card">
                  <span className="h-12 w-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                    <Icon className="h-6 w-6 text-primary" strokeWidth={1.75} />
                  </span>
                  <h3 className="font-heading text-lg text-foreground mt-5">{m.title}</h3>
                  <p className="text-sm text-foreground/70 mt-2 leading-relaxed">{m.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Private workspace */}
      <section className="mx-auto max-w-4xl px-5 sm:px-8 py-20 lg:py-24 text-center">
        <span className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
          <Lock className="h-7 w-7 text-primary" />
        </span>
        <h2 className="font-heading text-3xl sm:text-4xl text-foreground mt-6">Your own private workspace</h2>
        <p className="text-base text-foreground/75 mt-5 leading-relaxed max-w-2xl mx-auto">
          Every customer receives their own private, isolated workspace. Your content, numbers and
          documents are scoped to your account alone, never shared with or visible to another customer,
          and your AI Business Guide only ever sees your business. Your data stays yours, and it is always exportable.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 mt-8 text-sm text-foreground/70">
          <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /> Isolated per customer</span>
          <span className="inline-flex items-center gap-2"><Lock className="h-4 w-4 text-primary" /> Private to your account</span>
          <span className="inline-flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> Always exportable</span>
        </div>
      </section>

      {/* Founder Story */}
      <section className="bg-muted" data-testid="founder-story">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 py-20 lg:py-24">
          <span className="inline-flex items-center gap-2 text-primary">
            <Quote className="h-5 w-5" />
            <span className="text-xs font-semibold uppercase tracking-wide">Founder story</span>
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl text-foreground mt-4">Why I built Work &amp; Keep</h2>
          <div className="mt-6 space-y-4 text-base text-foreground/80 leading-relaxed max-w-2xl">
            <p>
              I built Work &amp; Keep while running my own business, Wholehearted Wellness. Too much of it was
              living across scattered tools, notes, memory and one-off AI conversations, and I was tired of
              holding the whole business in my head.
            </p>
            <p>
              I wanted one place where I could see where the business stood, what needed attention, what I was
              working on, and what to work on next. So I built it, and Wholehearted Wellness became the first
              real business I ran through the system.
            </p>
          </div>
          <p className="font-accent text-lg text-primary mt-8">— The founder of Work &amp; Keep</p>
          <p className="text-xs text-muted-foreground mt-2">
            Wholehearted Wellness is the founder&apos;s own business and real-world case study for Work &amp; Keep.
          </p>
        </div>
      </section>

      {/* Pricing */}
      <section className="bg-secondary text-secondary-foreground" id="pricing">
        <div className="mx-auto max-w-3xl px-5 sm:px-8 py-20 lg:py-24">
          <h2 className="font-heading text-3xl sm:text-4xl text-center">Simple, honest pricing</h2>
          <p className="text-center text-secondary-foreground/80 mt-4">One plan with everything included.</p>

          <div className="mt-12 rounded-[2rem] bg-card text-card-foreground p-8 sm:p-10 shadow-soft-lg" data-testid="pricing-card">
            <p className="text-sm font-semibold text-primary uppercase tracking-wide">Work &amp; Keep</p>
            <h3 className="font-heading text-3xl text-foreground mt-3">Start today</h3>
            <div className="mt-2 flex items-end gap-2">
              <span className="font-heading text-6xl text-foreground" data-testid="pricing-first">${firstPayment}</span>
              <span className="text-muted-foreground mb-3">due today</span>
            </div>
            <p className="text-foreground/80 mt-3">
              Includes your one-time <span className="font-semibold text-foreground" data-testid="pricing-setup">${setupFee}</span> setup &amp; onboarding fee and your first month.
            </p>
            <div className="mt-4 rounded-2xl bg-muted px-5 py-4 text-sm text-foreground/80">
              Then <span className="font-semibold text-foreground" data-testid="pricing-monthly">${monthly}</span>/month. Taxes may apply at checkout. Cancel anytime.
            </div>

            <div className="h-px bg-border my-8" />

            <ul className="space-y-3">
              {[
                "All modules and your full private workspace",
                "AI Business Guide grounded in your own business",
                "Guided onboarding and Business Setup",
                "Cancel anytime from the customer portal",
              ].map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm">
                  <span className="mt-0.5 h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <Check className="h-3 w-3 text-primary" />
                  </span>
                  <span className="text-foreground/90 leading-relaxed">{f}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 rounded-2xl border border-border bg-muted/50 px-5 py-4 text-xs text-foreground/70 leading-relaxed" data-testid="recurring-disclosure">
              $219 due today, which includes a $149 one time setup fee and the first $70 monthly subscription payment.
              Your Work &amp; Keep subscription continues at $70 per month until canceled. Cancel before your next
              billing date to prevent the next recurring charge. Cancellation is available through your billing portal.
              See <Link to="/terms" className="underline hover:text-foreground">Terms of Service</Link> for complete
              refund and cancellation terms.
            </div>

            <Button onClick={purchase} data-testid="pricing-purchase-button"
              className="w-full rounded-full h-12 mt-4 bg-primary hover:bg-primary/90 text-primary-foreground text-base gap-2">
              Get started <ArrowRight className="h-4 w-4" />
            </Button>
            <p className="text-xs text-muted-foreground text-center mt-4">
              Secure checkout via Stripe. Already purchased? <Link to="/login" className="underline hover:text-foreground">Sign in</Link>.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-5 sm:px-8 py-20 lg:py-24">
        <h2 className="font-heading text-3xl sm:text-4xl text-foreground text-center">Questions, answered</h2>
        <Accordion type="single" collapsible className="mt-10" data-testid="faq-accordion">
          {FAQ.map((item, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-border">
              <AccordionTrigger className="text-left font-medium text-foreground hover:no-underline" data-testid={`faq-trigger-${i}`}>
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-foreground/70 leading-relaxed">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Final CTA */}
      <section className="bg-primary text-primary-foreground">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 py-20 lg:py-24 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl leading-tight">
            Do the work. Keep the business together.
          </h2>
          <p className="text-primary-foreground/85 mt-5 max-w-xl mx-auto leading-relaxed">
            Organize your whole one-person business in one calm, private command center, discover grant
            opportunities that may match your profile, and work with an AI Guide that knows your business as well as you do.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={purchase} data-testid="final-purchase-button"
              className="rounded-full h-12 px-8 bg-card text-primary hover:bg-card/90 text-base gap-2">
              Get started, ${firstPayment} today <ArrowRight className="h-4 w-4" />
            </Button>
            <Link to="/login" data-testid="final-signin-link"
              className="inline-flex items-center justify-center rounded-full h-12 px-8 border border-primary-foreground/40 text-base font-medium hover:bg-primary-foreground/10 transition-colors">
              Sign in
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 py-10 flex flex-col items-center gap-6">
          <div className="w-full flex flex-col sm:flex-row items-center justify-between gap-4">
            <BrandWordmark className="text-2xl text-foreground" data-testid="footer-wordmark" />
            <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm" data-testid="footer-legal-nav">
              <Link to="/privacy" className="text-foreground/70 hover:text-foreground transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
              <Link to="/terms" className="text-foreground/70 hover:text-foreground transition-colors" data-testid="footer-terms-link">Terms of Service</Link>
              <a href="mailto:support@workandkeep.app" className="text-foreground/70 hover:text-foreground transition-colors" data-testid="footer-contact-link">Contact</a>
              <Link to="/login" className="text-foreground/70 hover:text-foreground transition-colors">Sign in</Link>
            </nav>
          </div>
          <p className="text-xs text-muted-foreground text-center">
            Work &amp; Keep is operated by Wholehearted Wellness LLC. &copy; {new Date().getFullYear()} Wholehearted Wellness LLC. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
